import { inject, observer } from 'mobx-react';
import React from 'react';
import { Button, Card, Form, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import ModelBaseNames from '../../../models/modelBase/ModelBaseNames';
import { signedPoints } from '../../../models/pointBasedForm/pointsRange';
import { Threshold } from '../../../models/ModelPerformances';
import Store from '../../../store';
import Error from '../../components/error/Error';
import {
  fetchScoreLookup,
  ScoreLookup,
  ScoreLookupTree,
} from '../../components/model/scoreLookupApi';
import { computationDisplayName } from '../../../utils';
import { overrideRuleSentence } from '../../components/model/overrideSentence';
import {
  nodeSegment,
  segmentColor,
  segmentTextColor,
  SEGMENT_LABEL,
} from '../../components/trees/nodeSegment';
import { weightText } from '../../components/model/scoreCardData';
import { NodeTraits } from '../../components/trees/NodeTraits';
import './scoreLookup.css';

type ScoreBand = { from: number; to: number; segment: Threshold };

/**
 * A tree based model's score bands, which are fixed for every tenant.
 *
 * `getCustomerFitMapping` on the server is the source of these numbers, and
 * smoothing maps a blended rate into the band its segment owns. They are NOT
 * the ensembling thresholds: those are on the conversion-rate scale, and
 * drawing the gauge from them put 49 in Very Good when 49 is the top of Low.
 */
const SCORE_BANDS: ScoreBand[] = [
  { from: 0, to: 49, segment: 'low' },
  { from: 50, to: 69, segment: 'medium' },
  { from: 70, to: 84, segment: 'good' },
  { from: 85, to: 100, segment: 'very good' },
];

/**
 * A point based model's bands, which ARE its thresholds.
 *
 * Nothing smooths a point based score: the total is what the thresholds cut
 * and what the CRM receives, so the scale runs from the lowest total the rules
 * can produce to the highest, and a band is dropped when its thresholds leave
 * it no width on that scale.
 */
function pointBands(
  thresholds: Record<string, number>,
  { min, max }: { min: number; max: number }
): ScoreBand[] {
  const cuts = [
    min,
    thresholds.medium,
    thresholds.good,
    thresholds['very good'],
    max,
  ].map((cut) => Math.min(max, Math.max(min, cut)));
  return (['low', 'medium', 'good', 'very good'] as Threshold[])
    .map((segment, index) => ({
      from: cuts[index],
      to: cuts[index + 1],
      segment,
    }))
    .filter((band) => band.to > band.from);
}

/** Past this, a value is cut and shown whole on hover. */
const MAX_VALUE_CHARS = 36;

/** A rule's reading of the record, short enough for one row. */
function ruleValue(value: string | number | null): {
  text: string;
  title?: string;
} {
  if (value === null || String(value).trim() === '') return { text: 'unknown' };
  const text = String(value);
  if (text.length <= MAX_VALUE_CHARS) return { text };
  return { text: `${text.slice(0, MAX_VALUE_CHARS).trimEnd()}…`, title: text };
}

/** How Salesforce renders the segment: stars, or a cross for Low. */
const SEGMENT_MARK: Record<Threshold, string> = {
  'very good': '⭐⭐⭐',
  good: '⭐⭐',
  medium: '⭐',
  low: '❌',
};

/**
 * The score on its model's scale, as an arc: 0-100 for a tree based model,
 * the reachable point totals for a point based one.
 */
function ScoreGauge({
  score,
  segment,
  bands = SCORE_BANDS,
  domain = { min: 0, max: 100 },
}: {
  score: number | null;
  segment: string | null;
  bands?: ScoreBand[];
  domain?: { min: number; max: number };
}) {
  const W = 230;
  const R = 92;
  const CX = W / 2;
  const CY = 106;
  const STROKE = 13;
  const span = domain.max - domain.min || 1;
  /** Half a stroke, in score points, plus air. See the bands below. */
  const CAP_INSET = (STROKE / 2 / R / (Math.PI / span)) * 1.12;

  const point = (value: number) => {
    const clamped = Math.min(domain.max, Math.max(domain.min, value));
    const angle = Math.PI * (1 - (clamped - domain.min) / span);
    return [CX + R * Math.cos(angle), CY - R * Math.sin(angle)];
  };
  const arc = (from: number, to: number) => {
    const [x1, y1] = point(from);
    const [x2, y2] = point(to);
    return `M ${x1} ${y1} A ${R} ${R} 0 0 1 ${x2} ${y2}`;
  };

  const marker = score === null ? null : point(score);
  return (
    <svg
      className="slk-gauge"
      viewBox={`0 0 ${W} ${CY + 14}`}
      role="img"
      aria-label={`Score ${score ?? 'unknown'}`}
    >
      {/* Round caps, inset far enough that they cannot reach each other.
          A round cap extends half the stroke past the end it caps, which in
          score units is (STROKE / 2) / R radians over pi/100 per point. Drawn
          at the true boundary the caps of two neighbours overlap into a lump;
          inset by that much plus a little, each band ends in its own curve
          with clear air between them. */}
      {bands
        .filter((band) => band.to - band.from > 2 * CAP_INSET)
        .map((band) => (
          <path
            key={band.segment}
            d={arc(band.from + CAP_INSET, band.to - CAP_INSET)}
            fill="none"
            stroke={segmentColor(band.segment) ?? '#dbe3ea'}
            strokeWidth={STROKE}
            strokeLinecap="round"
          />
        ))}
      {marker && (
        /* Where this lead sits. A dot on the band rather than a needle from
           the centre: the centre is where the number goes, and a needle
           crossing it competes with the one thing being read. */
        <circle
          cx={marker[0]}
          cy={marker[1]}
          r={7}
          fill="#fff"
          stroke="#17212e"
          strokeWidth={3}
        />
      )}
      <text x={CX} y={CY - 20} textAnchor="middle" className="slk-gauge-num">
        {score === null ? '—' : Math.round(score)}
      </text>
      <text x={CX} y={CY + 2} textAnchor="middle" className="slk-gauge-seg">
        {segment ? SEGMENT_LABEL[segment as Threshold] ?? segment : ''}
      </text>
    </svg>
  );
}

/** The segment chip every fit surface draws, in the fit palette. */
function SegmentChip({ segment }: { segment: string | null }) {
  if (!segment) return null;
  const tier = segment as Threshold;
  return (
    <span
      className="slk-chip"
      style={{
        background: segmentColor(tier) ?? undefined,
        color: segmentTextColor(tier) ?? undefined,
      }}
    >
      {SEGMENT_LABEL[tier] ?? segment}
    </span>
  );
}

/**
 * Score one record against this model, and say why.
 *
 * The platform answers this for live models only, because it asks predict and
 * predict reads config a deploy writes. Studio has the rules for a draft, so
 * this is the same question asked of the model you are still building.
 */
const ScoreLookupPage = inject('store')(
  observer(
    class ScoreLookupPage extends React.Component<
      { store?: Store },
      {
        email: string;
        loading: boolean;
        error: string | null;
        result: ScoreLookup | null;
        /** Which trees have their path open. Any number of them. */
        openTrees: number[];
      }
    > {
      constructor(props: { store?: Store }) {
        super(props);
        this.state = {
          email: '',
          loading: false,
          error: null,
          result: null,
          openTrees: [],
        };
        this.lookup = this.lookup.bind(this);
      }

      async lookup(event: React.FormEvent) {
        event.preventDefault();
        const email = this.state.email.trim();
        if (!email || this.state.loading) return;
        const { tenant, modelId } = this.props.store;
        this.setState({ loading: true, error: null });
        try {
          const result = await fetchScoreLookup(tenant, modelId, email);
          this.setState({
            result,
            loading: false,
            openTrees: [],
          });
        } catch (error) {
          this.setState({
            loading: false,
            result: null,
            error:
              error?.response?.data?.message ??
              'That lookup did not come back. The address may not enrich, or the model may have no dataset yet.',
          });
        }
      }

      /** A tree's own segment, from its leaf rate, using the shared mapping. */
      treeSegment(tree: ScoreLookupTree, thresholds: Record<string, number>) {
        if (tree.leafRate === null) return null;
        return nodeSegment(tree.leafRate / 100, thresholds);
      }

      /** The path through one tree, as a stepper, under the flow chart. */
      renderPath(result: ScoreLookup, tree?: ScoreLookupTree) {
        const { tenant, modelId } = this.props.store;
        if (!tree) return null;
        return (
          <div className="slk-flow" role="list">
            <span className="slk-flow-start" aria-hidden />
            {tree.path.map((step) => {
              const segment =
                step.reachedRate === null
                  ? null
                  : nodeSegment(step.reachedRate / 100, result.thresholds);
              const colour = segmentColor(segment);
              return (
                <React.Fragment key={step.nodeId}>
                  {/* The branch taken, on the wire. The tree renderer puts YES
                      and NO on its edges and the segment on its cards, and two
                      vocabularies for one structure is one to learn twice. */}
                  <span className="slk-flow-edge">
                    <span className="slk-flow-line" aria-hidden />
                    <span
                      className={
                        step.passed
                          ? 'slk-edge-tag slk-edge-tag--yes'
                          : 'slk-edge-tag slk-edge-tag--no'
                      }
                    >
                      {step.passed ? 'YES' : 'NO'}
                    </span>
                    <span className="slk-flow-line" aria-hidden />
                  </span>
                  <div
                    className="slk-node"
                    role="listitem"
                    style={colour ? { borderColor: colour } : undefined}
                  >
                    <div className="slk-node-head">
                      <SegmentChip segment={segment} />
                      {step.reachedRate !== null && (
                        <span className="slk-node-rate">
                          {step.reachedRate.toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <div className="slk-condition" title={step.condition}>
                      <NodeTraits condition={step.condition} max={4} />
                    </div>
                    <Link
                      className="slk-step-link"
                      to={`/tenant/${tenant}/models/${modelId}/model/trees/${tree.treeId}/nodes/${step.nodeId}`}
                    >
                      Open in tree
                      <i aria-hidden className="fas fa-arrow-right" />
                    </Link>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        );
      }

      /**
       * What lands in the CRM.
       *
       * The flow below explains how the score was reached; this is the answer
       * itself, in the shape the fields take once they are pushed. Someone
       * checking a lead is usually asking "what will the rep see", and that
       * question was only answerable by reading a number out of a diagram.
       */
      renderCrmView(result: ScoreLookup) {
        return (
          <div className="slk-result">
            <Card className="slk-card slk-fit">
              <Card.Body>
                <h6 className="slk-panel-title">Customer Fit</h6>
                {/* The stars Salesforce puts in the field, so the page and the
                    record show the same thing. Three, two, one, or a cross. */}
                <div className="slk-stars" title={result.segment ?? ''}>
                  {SEGMENT_MARK[result.segment as Threshold] ?? ''}
                </div>
                {result.base !== ModelBaseNames.trees && result.pointsRange ? (
                  <ScoreGauge
                    score={result.score}
                    segment={result.segment}
                    domain={result.pointsRange}
                    bands={pointBands(result.thresholds, result.pointsRange)}
                  />
                ) : (
                  <ScoreGauge score={result.score} segment={result.segment} />
                )}
                <div className="slk-fit-for">{result.email}</div>
              </Card.Body>
            </Card>

            <Card className="slk-card slk-signals">
              <Card.Body>
                <h6 className="slk-panel-title">Top signals</h6>
                {result.signals.length ? (
                  <ul className="slk-sigs">
                    {result.signals.map((signal) => (
                      <li
                        key={`${signal.group}-${signal.label}`}
                        className={
                          signal.direction === '-'
                            ? 'slk-sig slk-sig--neg'
                            : 'slk-sig'
                        }
                        title={signal.group}
                      >
                        {/* The mark IS the signal's direction. A list of ticks
                            where a third of them argue against the lead is a
                            list that says the opposite of what it means. */}
                        <span className="slk-sig-mark" aria-hidden>
                          {signal.direction === '-' ? '✕' : '✓'}
                        </span>
                        <span>{signal.label}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="slk-crm-none">
                    No signal definition matched this record.
                  </p>
                )}
              </Card.Body>
            </Card>
          </div>
        );
      }

      /**
       * The rule groups that fired, in place of the trees.
       *
       * One row per group, strongest first as predict ranks them, each naming
       * the attribute it read, this record's value and the points it added.
       * The total is not restated here: the score beside the table is the
       * total, and two numbers for one quantity is how they start to disagree.
       */
      renderRules(result: ScoreLookup) {
        if (!result.rules.length) {
          return (
            <div className="slk-rules">
              <p className="slk-crm-none">No rule matched this record.</p>
            </div>
          );
        }
        return (
          <div className="slk-rules">
            <table className="slk-rules-table" data-testid="score-lookup-rules">
              <tbody>
                {result.rules.map((rule, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={index}>
                    <th scope="row">
                      {rule.values
                        .map((read) => computationDisplayName(read.computation))
                        .join(' and ')}
                    </th>
                    <td className="slk-rule-value">
                      {rule.values.map((read) => {
                        const shown = ruleValue(read.value);
                        return (
                          <span key={read.computation} title={shown.title}>
                            {shown.text}
                          </span>
                        );
                      })}
                    </td>
                    <td
                      className={
                        rule.points < 0
                          ? 'slk-rule-points slk-rule-points--neg'
                          : 'slk-rule-points'
                      }
                    >
                      {signedPoints(rule.points)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="slk-rules-count">
              {result.rules.length} of {result.ruleGroups} rule groups fired
            </p>
          </div>
        );
      }

      /**
       * How the score is assembled, as a flow.
       *
       * Every tree is on screen at once, converging through its weight into the
       * blend, and the override is a stage of the same flow rather than a chip
       * tucked beside the score. The two halves are bracketed and named,
       * because "the model said Good and then a rule made it Low" is the single
       * thing people come to this page to find out, and a layout that does not
       * separate statistics from business rules cannot say it.
       *
       * The connectors are computed from the number of trees. The page this
       * follows positions each one with a hand-tuned rotation per tree count,
       * which is three special cases and no answer for a fourth tree.
       */
      renderAssembly(result: ScoreLookup) {
        const { tenant, modelId } = this.props.store;
        const overridden =
          result.overrideRuleIndex !== null && result.overrideMovedSegment;
        const trees = result.trees;
        const pointBased = result.base !== ModelBaseNames.trees;

        // Row geometry, so the curves can be drawn without measuring the DOM.
        // The open row carries its own path and is taller, so heights are per
        // row rather than one constant: a single ROW put the junction and the
        // wires at the wrong height the moment a path was shown.
        const ROW = 58;
        // Tall enough for a node card and its breathing room. The cards are a
        // fixed height, so this is not a guess about content.
        const OPEN_ROW = 238;
        // Real air once a path is open: three steppers stacked close read as
        // one grid of cards rather than as three routes.
        const GAP = trees.some((tree) =>
          this.state.openTrees.includes(tree.treeId)
        )
          ? 34
          : 10;
        const rowHeights = trees.map((tree) =>
          this.state.openTrees.includes(tree.treeId) ? OPEN_ROW : ROW
        );
        const tops = rowHeights.map((_, index) =>
          rowHeights.slice(0, index).reduce((sum, h) => sum + h + GAP, 0)
        );
        const height =
          rowHeights.reduce((sum, h) => sum + h, 0) + (trees.length - 1) * GAP;
        const centre = (index: number) => tops[index] + rowHeights[index] / 2;
        const mid = height / 2;

        return (
          <Card className="slk-card">
            <Card.Body>
              <h5 className="slk-title">Score</h5>

              {/* Two elements on purpose: the outer one scrolls, the inner one
                  is the row and is allowed to be wider than it. A single
                  element cannot be both, which is why the score and the
                  override were cut off once a path opened instead of the chart
                  scrolling to reach them. */}
              <div className="slk-flowchart">
                <div className="slk-flowrow">
                  <div className="slk-stats">
                    {pointBased && this.renderRules(result)}
                    {!pointBased && (
                      <>
                        <div className="slk-tree-col" style={{ height }}>
                          {trees.map((tree, index) => {
                            const open = this.state.openTrees.includes(
                              tree.treeId
                            );
                            return (
                              <div
                                className="slk-tree-row"
                                key={tree.treeId}
                                style={{ height: rowHeights[index] }}
                              >
                                <button
                                  type="button"
                                  className={
                                    open ? 'slk-tree slk-tree--on' : 'slk-tree'
                                  }
                                  aria-expanded={open}
                                  onClick={() =>
                                    this.setState({
                                      openTrees: open
                                        ? this.state.openTrees.filter(
                                            (id) => id !== tree.treeId
                                          )
                                        : [
                                            ...this.state.openTrees,
                                            tree.treeId,
                                          ],
                                    })
                                  }
                                >
                                  <span className="slk-tree-name">
                                    Tree {tree.treeId}
                                  </span>
                                  <SegmentChip
                                    segment={this.treeSegment(
                                      tree,
                                      result.thresholds
                                    )}
                                  />
                                  <span className="slk-tree-more">
                                    {open ? 'Hide path' : 'Show path'}
                                  </span>
                                </button>
                                {/* Straight to the leaf this record landed in, for
                              the reader who wants the node rather than the
                              route. Titled with what a tree node's own hover
                              says, so the two agree about that node. */}
                                {tree.leafId !== null && (
                                  <Link
                                    className="slk-leaf-link"
                                    title={
                                      tree.leafRate === null
                                        ? `Leaf ${tree.leafId}`
                                        : `Leaf ${tree.leafId} · ${
                                            SEGMENT_LABEL[
                                              this.treeSegment(
                                                tree,
                                                result.thresholds
                                              ) as Threshold
                                            ] ?? ''
                                          } · converts at ${tree.leafRate.toFixed(
                                            1
                                          )}%`
                                    }
                                    to={`/tenant/${tenant}/models/${modelId}/model/trees/${tree.treeId}/nodes/${tree.leafId}`}
                                  >
                                    Leaf {tree.leafId}
                                    <i
                                      aria-hidden
                                      className="fas fa-arrow-right"
                                    />
                                  </Link>
                                )}
                                {/* The path IS the route from this tree to the
                              junction, so it belongs on the wire rather than
                              in a section underneath that repeats the tree's
                              name to say which one it describes. */}
                                {open && this.renderPath(result, tree)}
                              </div>
                            );
                          })}
                        </div>

                        {/* The weights, drawn. One curve per tree, from its centre to
                      the junction, with its share on the curve. */}
                        <svg
                          className="slk-wires"
                          width="104"
                          height={height}
                          viewBox={`0 0 104 ${height}`}
                          aria-hidden
                        >
                          {trees.map((tree, index) => {
                            const y = centre(index);
                            return (
                              <g key={tree.treeId}>
                                {/* Solid, and its width IS the weight. The tree
                              renderer already scales its links this way, and a
                              dash said "provisional" about the one part of the
                              chart that is exact. */}
                                <path
                                  d={`M 0 ${y} C 52 ${y}, 52 ${mid}, 104 ${mid}`}
                                  fill="none"
                                  stroke={
                                    tree.weight > 0 ? '#9fb2c4' : '#dde5ec'
                                  }
                                  strokeWidth={
                                    tree.weight > 0 ? 1 + tree.weight * 5 : 1
                                  }
                                  strokeLinecap="round"
                                />
                                {/* At the START of the wire, on its own row. At the
                              midpoint the labels sat where the curves bunch
                              together and crossed both them and each other. */}
                                <rect
                                  x="4"
                                  y={y - 9}
                                  width="34"
                                  height="18"
                                  rx="9"
                                  fill="#f4f7fa"
                                />
                                <text
                                  x="21"
                                  y={y + 4}
                                  textAnchor="middle"
                                  className={
                                    tree.weight > 0
                                      ? 'slk-wire-label'
                                      : 'slk-wire-label slk-wire-label--none'
                                  }
                                >
                                  {weightText(tree.weight)}
                                </text>
                              </g>
                            );
                          })}
                        </svg>
                      </>
                    )}

                    <div
                      className="slk-blend"
                      style={pointBased ? undefined : { height }}
                    >
                      {/* The score BEFORE the override, because smoothing maps a
                        rate into its segment's band: forcing the segment moves
                        the number with it, and showing the final score here
                        would make the override look like it changed nothing. */}
                      <div className="slk-score">
                        {(overridden
                          ? result.scoreBeforeOverrides
                          : result.score) === null
                          ? '—'
                          : Math.round(
                              (overridden
                                ? result.scoreBeforeOverrides
                                : result.score) as number
                            )}
                      </div>
                      <SegmentChip
                        segment={
                          overridden
                            ? result.segmentBeforeOverrides
                            : result.segment
                        }
                      />
                    </div>
                  </div>

                  {overridden && (
                    <div className="slk-business">
                      <span className="slk-wire-flat" aria-hidden />
                      {/* What the rule SAYS, not a link to go and read it. The
                        question this answers is "why is it Low", and an answer
                        that costs a page change is not one. Rendered with the
                        same helper the Fine tuning card uses, so the two
                        cannot describe one rule differently. */}
                      <div
                        className="slk-override"
                        style={
                          segmentColor(result.segment as Threshold)
                            ? {
                                borderColor: segmentColor(
                                  result.segment as Threshold
                                ),
                              }
                            : undefined
                        }
                      >
                        <div className="slk-override-head">
                          {/* The segment the RULE forces, not the one the record
                            ended on. They coincide whenever the override moved
                            it, which is the common case and why reading
                            `result.segment` here looked right; they differ on a
                            rule that fires without moving the segment, and on a
                            ceiling or floor rule that only bounds it. */}
                          <SegmentChip
                            segment={
                              result.overrideRule?.segment ?? result.segment
                            }
                          />
                          <span className="slk-override-num">
                            Rule {result.overrideRuleIndex + 1}
                          </span>
                        </div>
                        <div className="slk-override-when">
                          {result.overrideRule
                            ? overrideRuleSentence(result.overrideRule) ||
                              'no conditions'
                            : 'matched'}
                        </div>
                        {/* The operator AND what it asks for. Rendering the
                          operator alone left the card reading "should be" with
                          nothing after it, a sentence cut off mid-clause. */}
                        <div className="slk-override-then">
                          {result.overrideRule?.rule ?? 'should be'}{' '}
                          <span className="slk-override-target">
                            {result.overrideRule?.segment ?? result.segment}
                          </span>
                        </div>
                        <Link
                          className="slk-override-link"
                          to={`/tenant/${tenant}/models/${modelId}/model/tuning`}
                        >
                          Edit on Fine tuning
                          <i aria-hidden className="fas fa-arrow-right" />
                        </Link>
                      </div>
                      <span className="slk-wire-flat" aria-hidden />
                      <div className="slk-final">
                        <div className="slk-score slk-score--final">
                          {result.score === null
                            ? '—'
                            : Math.round(result.score)}
                        </div>
                        <SegmentChip segment={result.segment} />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Which half is which. The distinction the page exists to make. */}
              <div className="slk-zones">
                <div className="slk-zone">
                  <span className="slk-zone-rule" aria-hidden />
                  <span className="slk-zone-name">
                    {pointBased ? 'Rule points' : 'Historical scoring'}
                  </span>
                </div>
                {overridden && (
                  <div className="slk-zone slk-zone--business">
                    <span className="slk-zone-rule" aria-hidden />
                    <span className="slk-zone-name">Business overrides</span>
                  </div>
                )}
              </div>

              {result.overrideRuleIndex !== null && !overridden && (
                <p className="slk-foot">
                  Rule {result.overrideRuleIndex + 1} matched, and asked for the
                  segment the {pointBased ? 'rules' : 'trees'} already gave.
                </p>
              )}
            </Card.Body>
          </Card>
        );
      }

      render() {
        const { result, loading, error } = this.state;
        return (
          <div className="slk mt-3 mb-5">
            <Card className="slk-card">
              <Card.Body>
                <h5 className="slk-title">Score lookup</h5>
                <p className="slk-scope">
                  Scored against this model as it stands, deployed or not.
                </p>
                <Form className="slk-form" onSubmit={this.lookup}>
                  <Form.Control
                    type="email"
                    value={this.state.email}
                    placeholder="someone@company.com"
                    disabled={loading}
                    onChange={(event) =>
                      this.setState({ email: event.target.value })
                    }
                  />
                  <Button
                    type="submit"
                    disabled={loading || !this.state.email.trim()}
                  >
                    {loading ? 'Scoring...' : 'Look up'}
                  </Button>
                </Form>
              </Card.Body>
            </Card>

            {error && <Error message={error} />}

            {loading && (
              <div className="slk-loading">
                <Spinner animation="border" size="sm" />
                <span>
                  Enriching the record and running it through the model...
                </span>
              </div>
            )}

            {result && !loading && (
              <>
                {this.renderCrmView(result)}
                {this.renderAssembly(result)}
              </>
            )}
          </div>
        );
      }
    }
  )
);

export default ScoreLookupPage;
